import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardHeader,
  Title,
  Text,
  Label,
  Input,
  Select,
  Option,
  Button,
  FlexBox,
  MessageStrip,
  Dialog,
  Bar,
  Icon,
  BusyIndicator
} from '@ui5/webcomponents-react';
import { organizationApi, authApi } from '../services/api';
import { Organization, OrganizationUpdate, Industry, EmployeeCount } from '@dsgvo-buddy/types';
import { ObjectStatus } from '@ui5/webcomponents-react';
import '@ui5/webcomponents-icons/dist/AllIcons.js';
import { formatRetention } from '../helpers/formatRetention';

const InfoRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <>
    <Text style={{ fontWeight: '600', color: 'var(--sapNeutralTextColor)', fontSize: '0.8125rem' }}>{label}</Text>
    <div>{children}</div>
  </>
);

export default function OrganizationPage() {
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editFormData, setEditFormData] = useState<OrganizationUpdate>({});
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [marketingConsent, setMarketingConsent] = useState<boolean | null>(null);

  useEffect(() => { loadData(); loadMarketingConsent(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const orgsResponse = await organizationApi.getAll();
      if (orgsResponse.success && orgsResponse.data) {
        setOrganizations(orgsResponse.data);
        if (orgsResponse.data.length > 0) setSelectedOrg(orgsResponse.data[0]);
      } else {
        setError(orgsResponse.error || 'Fehler beim Laden');
      }
    } catch {
      setError('Ein unerwarteter Fehler ist aufgetreten');
    } finally {
      setLoading(false);
    }
  };

  const loadMarketingConsent = async () => {
    const res = await authApi.getCurrentUser();
    if (res.success && res.data) {
      setMarketingConsent(!!(res.data as any).marketing_attribution_consent);
    }
  };

  const handleRevokeMarketingConsent = async () => {
    if (!confirm('Einwilligung widerrufen? Zukünftige Werbe-Attributionsdaten werden nicht mehr an Google übermittelt.')) return;
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';
    const token = localStorage.getItem('auth_token');
    const res = await fetch(`${apiUrl}/account/marketing-consent`, {
      method: 'DELETE',
      credentials: 'include',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
    });
    const data = await res.json();
    if (data.success) {
      setMarketingConsent(false);
      setSuccess(`Marketing-Einwilligung widerrufen. ${data.data?.revokedConversions || 0} noch nicht übermittelte Einträge wurden gelöscht.`);
    }
  };

  const openEditDialog = () => {
    if (selectedOrg) {
      setEditFormData({
        name: selectedOrg.name,
        address: selectedOrg.address,
        contact_person: selectedOrg.contact_person,
        contact_email: selectedOrg.contact_email,
        contact_phone: selectedOrg.contact_phone || '',
        dsb_name: selectedOrg.dsb_name || '',
        dsb_email: selectedOrg.dsb_email || '',
        dsb_phone: selectedOrg.dsb_phone || '',
        industry: selectedOrg.industry || undefined,
        employee_count: selectedOrg.employee_count || undefined,
        default_language: selectedOrg.default_language,
        default_retention_period: selectedOrg.default_retention_period
      });
      setDialogError(null);
      setEditDialogOpen(true);
    }
  };

  const closeEditDialog = () => {
    setEditDialogOpen(false);
    setDialogError(null);
  };

  const handleSaveEdit = async () => {
    if (!selectedOrg) return;

    // Client-side validation — show errors inside the dialog
    const name = (editFormData.name || '').trim();
    const address = (editFormData.address || '').trim();
    const contactPerson = (editFormData.contact_person || '').trim();
    const contactEmail = (editFormData.contact_email || '').trim();
    const dsbName = (editFormData.dsb_name || '').trim();
    const dsbEmail = (editFormData.dsb_email || '').trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!name) { setDialogError('Unternehmensname ist erforderlich'); return; }
    if (!address) { setDialogError('Adresse ist erforderlich'); return; }
    if (!contactPerson) { setDialogError('Ansprechpartner ist erforderlich'); return; }
    if (!contactEmail || !emailRegex.test(contactEmail)) { setDialogError('Gültige E-Mail für Ansprechpartner erforderlich'); return; }
    if (!dsbName) { setDialogError('Datenschutzbeauftragter (Name) ist erforderlich'); return; }
    if (!dsbEmail || !emailRegex.test(dsbEmail)) { setDialogError('Gültige E-Mail für Datenschutzbeauftragten erforderlich'); return; }

    setDialogError(null);
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const response = await organizationApi.update(selectedOrg.id, editFormData);
      if (response.success && response.data) {
        setSelectedOrg(response.data);
        setOrganizations(organizations.map(org => org.id === response.data!.id ? response.data! : org));
        setSuccess('Organisation erfolgreich aktualisiert');
        setEditDialogOpen(false);
      } else {
        setDialogError(response.error || 'Fehler beim Aktualisieren');
      }
    } catch {
      setDialogError('Ein unerwarteter Fehler ist aufgetreten');
    } finally {
      setLoading(false);
    }
  };

  const updateEditField = (field: keyof OrganizationUpdate, value: any) => {
    setEditFormData(prev => ({ ...prev, [field]: value }));
  };

  const getIndustryLabel = (industry: Industry | null) => {
    if (!industry) return '-';
    const labels: Record<Industry, string> = {
      [Industry.IT]: 'IT & Technologie',
      [Industry.HEALTHCARE]: 'Gesundheitswesen',
      [Industry.FINANCE]: 'Finanzwesen',
      [Industry.RETAIL]: 'Einzelhandel',
      [Industry.MANUFACTURING]: 'Produktion',
      [Industry.SERVICES]: 'Dienstleistungen',
      [Industry.EDUCATION]: 'Bildung',
      [Industry.GOVERNMENT]: 'Öffentliche Verwaltung',
      [Industry.OTHER]: 'Sonstiges'
    };
    return labels[industry];
  };

  const getEmployeeCountLabel = (count: EmployeeCount | null) => {
    if (!count) return '-';
    const labels: Record<EmployeeCount, string> = {
      [EmployeeCount.SMALL]: '1-10 Mitarbeiter',
      [EmployeeCount.MEDIUM]: '11-50 Mitarbeiter',
      [EmployeeCount.LARGE]: '51-250 Mitarbeiter',
      [EmployeeCount.XLARGE]: '251-1000 Mitarbeiter',
      [EmployeeCount.ENTERPRISE]: '1000+ Mitarbeiter'
    };
    return labels[count];
  };

  if (loading && organizations.length === 0) {
    return (
      <FlexBox alignItems="Center" justifyContent="Center" style={{ height: '100%' }}>
        <BusyIndicator active size="Large" />
      </FlexBox>
    );
  }

  if (!selectedOrg && !loading) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem' }}>
        <Icon name="factory" style={{ fontSize: '3rem', color: 'var(--sapNeutralColor)', marginBottom: '1rem' }} />
        <Title level="H4" style={{ marginBottom: '0.5rem' }}>Keine Organisation gefunden</Title>
        <Text style={{ display: 'block', marginBottom: '1rem' }}>Sie haben noch keine Organisation angelegt.</Text>
        <Button design="Emphasized" onClick={() => navigate('/setup')}>Organisation einrichten</Button>
      </div>
    );
  }

  return (
    <div className="page-layout--form">
      <FlexBox justifyContent="SpaceBetween" alignItems="Center" style={{ marginBottom: '1.5rem' }}>
        <div>
          <Title level="H3" style={{ marginBottom: '0.25rem' }}>Organisationsverwaltung</Title>
          <Text style={{ color: 'var(--sapNeutralTextColor)' }}>{selectedOrg?.name}</Text>
        </div>
        <Button design="Emphasized" icon="edit" onClick={openEditDialog} disabled={!selectedOrg}>
          Bearbeiten
        </Button>
      </FlexBox>

      {error && <MessageStrip design="Negative" hideCloseButton style={{ marginBottom: '1rem' }}>{error}</MessageStrip>}
      {success && <MessageStrip design="Positive" hideCloseButton style={{ marginBottom: '1rem' }}>{success}</MessageStrip>}

      {selectedOrg && (
        <FlexBox direction="Column" style={{ gap: '1rem' }}>
          <Card header={<CardHeader titleText="Unternehmensdaten" avatar={<Icon name="factory" />} />}>
            <div style={{ padding: '0.25rem 1.25rem 1.25rem', display: 'grid', gridTemplateColumns: '180px 1fr', gap: '0.75rem 1.5rem', alignItems: 'baseline' }}>
              <InfoRow label="Name"><Text>{selectedOrg.name}</Text></InfoRow>
              <InfoRow label="Adresse"><Text>{selectedOrg.address}</Text></InfoRow>
              <InfoRow label="Branche"><Text>{getIndustryLabel(selectedOrg.industry)}</Text></InfoRow>
              <InfoRow label="Mitarbeiterzahl"><Text>{getEmployeeCountLabel(selectedOrg.employee_count)}</Text></InfoRow>
            </div>
          </Card>

          <Card header={<CardHeader titleText="Ansprechpartner" avatar={<Icon name="person-placeholder" />} />}>
            <div style={{ padding: '0.25rem 1.25rem 1.25rem', display: 'grid', gridTemplateColumns: '180px 1fr', gap: '0.75rem 1.5rem', alignItems: 'baseline' }}>
              <InfoRow label="Name"><Text>{selectedOrg.contact_person}</Text></InfoRow>
              <InfoRow label="E-Mail"><Text>{selectedOrg.contact_email}</Text></InfoRow>
              <InfoRow label="Telefon"><Text>{selectedOrg.contact_phone || '-'}</Text></InfoRow>
            </div>
          </Card>

          <Card header={<CardHeader titleText="Datenschutzbeauftragter" avatar={<Icon name="shield" />} />}>
            <div style={{ padding: '0.25rem 1.25rem 1.25rem', display: 'grid', gridTemplateColumns: '180px 1fr', gap: '0.75rem 1.5rem', alignItems: 'baseline' }}>
              <InfoRow label="Name"><Text>{selectedOrg.dsb_name || 'Nicht angegeben'}</Text></InfoRow>
              <InfoRow label="E-Mail"><Text>{selectedOrg.dsb_email || 'Nicht angegeben'}</Text></InfoRow>
              <InfoRow label="Telefon"><Text>{selectedOrg.dsb_phone || 'Nicht angegeben'}</Text></InfoRow>
            </div>
          </Card>

          <Card header={<CardHeader titleText="Konfiguration" avatar={<Icon name="action-settings" />} />}>
            <div style={{ padding: '0.25rem 1.25rem 1.25rem', display: 'grid', gridTemplateColumns: '180px 1fr', gap: '0.75rem 1.5rem', alignItems: 'baseline' }}>
              <InfoRow label="Standardsprache"><Text>{selectedOrg.default_language === 'de' ? 'Deutsch' : 'Englisch'}</Text></InfoRow>
              <InfoRow label="Standard-Löschfrist"><Text>{formatRetention(selectedOrg.default_retention_period)}</Text></InfoRow>
            </div>
          </Card>
        </FlexBox>
      )}

      {/* Marketing Attribution */}
      {marketingConsent !== null && (
        <Card style={{ marginTop: '1.5rem' }}>
          <CardHeader titleText="Marketing-Attribution (Google Ads)" avatar={<Icon name="marketing-campaign" />} />
          <div style={{ padding: '1rem 1.25rem 1.25rem' }}>
            {marketingConsent ? (
              <>
                <FlexBox alignItems="Center" style={{ gap: '0.5rem', marginBottom: '0.75rem' }}>
                  <ObjectStatus state="Success" showDefaultIcon>Einwilligung erteilt</ObjectStatus>
                </FlexBox>
                <Text style={{ display: 'block', marginBottom: '1rem', color: 'var(--sapNeutralTextColor)', fontSize: '0.875rem', lineHeight: 1.6 }}>
                  Sie haben der pseudonymen Zuordnung Ihrer Registrierung zu einer Google-Werbeanzeige zugestimmt.
                  Es werden ausschließlich pseudonyme Klick-IDs (keine personenbezogenen Daten) an Google übermittelt.
                </Text>
                <Button design="Negative" icon="decline" onClick={handleRevokeMarketingConsent}>
                  Einwilligung widerrufen
                </Button>
              </>
            ) : (
              <Text style={{ color: 'var(--sapNeutralTextColor)', fontSize: '0.875rem', lineHeight: 1.6 }}>
                Keine aktive Einwilligung zur Marketing-Attribution. Es werden keine Daten an Google übertragen.
              </Text>
            )}
          </div>
        </Card>
      )}

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onAfterClose={closeEditDialog}
        headerText="Organisation bearbeiten"
        footer={
          <Bar endContent={
            <>
              <Button design="Transparent" onClick={closeEditDialog}>Abbrechen</Button>
              <Button design="Emphasized" onClick={handleSaveEdit}>Speichern</Button>
            </>
          } />
        }
      >
        <div style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: '1rem', minWidth: '500px' }}>
          {dialogError && (
            <MessageStrip design="Negative" hideCloseButton>{dialogError}</MessageStrip>
          )}
          <div>
            <Label required style={{ display: 'block', marginBottom: '0.25rem' }}>Unternehmensname</Label>
            <Input value={editFormData.name || ''} onInput={(e: any) => updateEditField('name', e.target.value)} style={{ width: '100%' }} />
          </div>
          <div>
            <Label required style={{ display: 'block', marginBottom: '0.25rem' }}>Adresse</Label>
            <Input value={editFormData.address || ''} onInput={(e: any) => updateEditField('address', e.target.value)} style={{ width: '100%' }} />
          </div>
          <div>
            <Label style={{ display: 'block', marginBottom: '0.25rem' }}>Branche</Label>
            <Select value={editFormData.industry || ''} onChange={(e: any) => updateEditField('industry', e.detail.selectedOption.value || undefined)} style={{ width: '100%' }}>
              <Option value="">Bitte wählen...</Option>
              <Option value={Industry.IT}>IT & Technologie</Option>
              <Option value={Industry.HEALTHCARE}>Gesundheitswesen</Option>
              <Option value={Industry.FINANCE}>Finanzwesen</Option>
              <Option value={Industry.RETAIL}>Einzelhandel</Option>
              <Option value={Industry.MANUFACTURING}>Produktion</Option>
              <Option value={Industry.SERVICES}>Dienstleistungen</Option>
              <Option value={Industry.EDUCATION}>Bildung</Option>
              <Option value={Industry.GOVERNMENT}>Öffentliche Verwaltung</Option>
              <Option value={Industry.OTHER}>Sonstiges</Option>
            </Select>
          </div>
          <div>
            <Label style={{ display: 'block', marginBottom: '0.25rem' }}>Mitarbeiterzahl</Label>
            <Select value={editFormData.employee_count || ''} onChange={(e: any) => updateEditField('employee_count', e.detail.selectedOption.value || undefined)} style={{ width: '100%' }}>
              <Option value="">Bitte wählen...</Option>
              <Option value={EmployeeCount.SMALL}>1-10 Mitarbeiter</Option>
              <Option value={EmployeeCount.MEDIUM}>11-50 Mitarbeiter</Option>
              <Option value={EmployeeCount.LARGE}>51-250 Mitarbeiter</Option>
              <Option value={EmployeeCount.XLARGE}>251-1000 Mitarbeiter</Option>
              <Option value={EmployeeCount.ENTERPRISE}>1000+ Mitarbeiter</Option>
            </Select>
          </div>
          <div>
            <Label required style={{ display: 'block', marginBottom: '0.25rem' }}>Ansprechpartner</Label>
            <Input value={editFormData.contact_person || ''} onInput={(e: any) => updateEditField('contact_person', e.target.value)} style={{ width: '100%' }} />
          </div>
          <div>
            <Label required style={{ display: 'block', marginBottom: '0.25rem' }}>E-Mail</Label>
            <Input type="Email" value={editFormData.contact_email || ''} onInput={(e: any) => updateEditField('contact_email', e.target.value)} style={{ width: '100%' }} />
          </div>
          <div>
            <Label style={{ display: 'block', marginBottom: '0.25rem' }}>Telefon</Label>
            <Input value={editFormData.contact_phone || ''} onInput={(e: any) => updateEditField('contact_phone', e.target.value)} style={{ width: '100%' }} />
          </div>
          <div>
            <Label required style={{ display: 'block', marginBottom: '0.25rem' }}>Datenschutzbeauftragter (Name)</Label>
            <Input value={editFormData.dsb_name || ''} onInput={(e: any) => updateEditField('dsb_name', e.target.value)} style={{ width: '100%' }} />
          </div>
          <div>
            <Label required style={{ display: 'block', marginBottom: '0.25rem' }}>Datenschutzbeauftragter (E-Mail)</Label>
            <Input type="Email" value={editFormData.dsb_email || ''} onInput={(e: any) => updateEditField('dsb_email', e.target.value)} style={{ width: '100%' }} />
          </div>
          <div>
            <Label style={{ display: 'block', marginBottom: '0.25rem' }}>Datenschutzbeauftragter (Telefon)</Label>
            <Input value={editFormData.dsb_phone || ''} onInput={(e: any) => updateEditField('dsb_phone', e.target.value)} style={{ width: '100%' }} />
          </div>
          <div>
            <Label style={{ display: 'block', marginBottom: '0.25rem' }}>Standardsprache</Label>
            <Select
              value={editFormData.default_language || 'de'}
              onChange={(e: any) => updateEditField('default_language', e.detail.selectedOption.value)}
              style={{ width: '100%' }}
            >
              <Option value="de">Deutsch</Option>
              <Option value="en">Englisch</Option>
            </Select>
          </div>
          <div>
            <Label style={{ display: 'block', marginBottom: '0.25rem' }}>Standard-Löschfrist (Monate)</Label>
            <Input type="Number" value={editFormData.default_retention_period?.toString() || '36'} onInput={(e: any) => updateEditField('default_retention_period', parseInt(e.target.value) || 36)} style={{ width: '100%' }} />
          </div>
        </div>
      </Dialog>
    </div>
  );
}
